import React, { useState } from "react";

import { strings } from "@perceptron/constants";
import { Skeleton } from "@perceptron/ui/components/skeleton";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import Image from "next/image";

import { THUMB_MAX } from "@demo/constants/media-set-layout";
import { MediaData } from "@demo/types/base";
import { getImageSrc } from "@demo/utils/media";

/** The widest a media-set cell grows to; see MediaSetCollection's THUMB_MAX. */

interface ImageThumbnailProps {
  media: MediaData;
  alt?: string;
  index?: number;
  className?: string;
  loading?: "eager" | "lazy";
}

export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({ media, alt, index = 0, className = "", loading }) => {
  const [isLoading, setIsLoading] = useState(true);
  const imageSrc = getImageSrc(media);
  const altText = alt || formatString(strings.imageRenderer.imageAlt, index + 1);

  return (
    <div className={cn("relative h-full w-full overflow-hidden rounded-lg bg-background", className)}>
      {imageSrc ? (
        <>
          {isLoading && <Skeleton className="absolute inset-0 size-full" />}
          <Image
            alt={altText}
            className="object-cover"
            fill
            loading={loading}
            sizes={`${THUMB_MAX}px`}
            src={imageSrc}
            onError={() => setIsLoading(false)}
            onLoad={() => setIsLoading(false)}
          />
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gray-200 text-xs text-gray-500">
          {strings.imageRenderer.invalidImage}
        </div>
      )}
    </div>
  );
};
